import { useCallback } from "react";
import { useAIChat } from "./useAIChat";
import { createSessionId } from "@/app/utils/string";
import { useCurrentUser } from "@/app/lib/AuthProvider";
import { useMessageStore } from "@/app/store";
import {
  MessageSender,
  MessageType,
  HeaderType,
  RobotMessageType,
} from "@/app/store/types";
import {
  ReportBizTypeEnum,
  ReportKeywordContentEnum,
  ReportParentContentEnum,
} from "@/app/types/typeConfig/report";
import { ScrollAction, ScrollFrom } from "@/app/hooks/useReadScrollToBottom";
import { useTranslations } from "@/app/lib/LocaleProvider";
import { isOversea } from "@/const/systemConfig";
import { message } from "antd";

export interface UseSSESendOptions {
  /**
   * 发送成功后的滚动处理函数（可选）
   */
  handleScrollToBottom?: (action: ScrollAction, from: ScrollFrom) => void;
  /**
   * 发送成功后的回调（可选）
   */
  onSendSuccess?: (content: string) => void;
  /**
   * 发送失败后的回调（可选）
   */
  onSendError?: (error: unknown, content: string) => void;
}

export interface SSESendParams {
  content: string;
  bizType: ReportBizTypeEnum;
  contentType: ReportKeywordContentEnum;
  parentContent?: string;
  parentContentId?: string;
  parentContentType?: ReportParentContentEnum;
}

export interface UseSSESendReturn {
  /**
   * SSE方式发送消息
   * @param content - 消息内容，可以是字符串或完整的参数对象
   * @param checkFirstMsg - 是否检查并发送首次AI回复
   * @param handyHmsOnly - 是否是handy侧的hms消息，如果是，则不展示在消息列表中
   */
  handleSSESend: (
    content: string | SSESendParams,
    checkFirstMsg?: boolean,
    handyHmsOnly?: boolean,
  ) => Promise<void>;
  /**
   * 是否正在发送中
   */
  isSending: boolean;
  /**
   * 停止当前SSE流式响应
   */
  stopStreaming: () => void;
}

/**
 * SSE发送消息的自定义Hook
 *
 * @example
 * ```tsx
 * const { handleSSESend, isSending } = useSSESend({
 *   handleScrollToBottom: (action, from) => {
 *     // 处理滚动
 *   },
 *   onSendSuccess: (content) => {
 *     console.log('发送成功:', content);
 *   }
 * });
 *
 * // 使用方式1：直接传入字符串
 * await handleSSESend("Hello");
 *
 * // 使用方式2：传入完整参数
 * await handleSSESend({
 *   content: "Hello",
 *   bizType: ReportBizTypeEnum.AI_CHAT,
 *   parentContent: "parent content"
 * });
 * ```
 */
export function useSSESend(options: UseSSESendOptions = {}): UseSSESendReturn {
  const { handleScrollToBottom, onSendSuccess, onSendError } = options;

  const user = useCurrentUser();
  const sessionId = createSessionId();
  const t = useTranslations();
  const { setRobotMessage, hasUserSentFirstMessage, globalStopStreaming } =
    useMessageStore();

  const {
    sendMessage,
    isStreaming,
    stopStreaming: localStopStreaming,
  } = useAIChat({
    url: "/gw/robot/sse/connect",
    sendUrl: "/gw/robot/sse/chat",
    timeout: 60000,
    debug: true,
    sessionId: user?.cusId || sessionId,
  });

  // 包装的停止函数：优先使用全局的 stopStreaming（跨组件共享）
  const stopStreaming = useCallback(() => {
    if (globalStopStreaming) {
      // 使用全局的 stopStreaming（可能是其他组件注册的）
      globalStopStreaming();
    } else {
      // 回退到本地的 stopStreaming
      localStopStreaming();
    }
  }, [globalStopStreaming, localStopStreaming]);

  const handleSSESend = useCallback(
    async (
      contentOrParams: string | SSESendParams,
      checkFirstMsg = false,
      handyHmsOnly = false,
    ) => {
      if (useMessageStore.getState().isAIStreaming) {
        message.warning(t.selectHotAIQuestion.replying);
        return;
      }

      // 处理参数，支持字符串或对象
      const params: SSESendParams =
        typeof contentOrParams === "string"
          ? {
              content: contentOrParams,
              bizType: ReportBizTypeEnum.AI_CHAT,
              contentType: ReportKeywordContentEnum.AI_KEYWORD,
              parentContent: contentOrParams,
              parentContentId: "",
              parentContentType: ReportParentContentEnum.DIRECT_SEARCH,
            }
          : {
              parentContentId: "",
              parentContentType: ReportParentContentEnum.DIRECT_SEARCH,
              ...contentOrParams,
              parentContent:
                contentOrParams.parentContent || contentOrParams.content,
            };

      const content = params.content;

      // 检查内容是否为空
      if (!content.trim()) {
        return;
      }

      try {
        await sendMessage(
          params,
          () => {
            // 发送成功后的处理
            if (handleScrollToBottom) {
              handleScrollToBottom(
                ScrollAction.FORCE_TO_BOTTOM,
                ScrollFrom.SEND_MSG,
              );
            }

            if (onSendSuccess) {
              onSendSuccess(content);
            }
          },
          false,
          handyHmsOnly,
        );

        // 仅在传递 checkFirstMsg 时判断是否发送首次AI回复
        if (checkFirstMsg && !hasUserSentFirstMessage) {
          setRobotMessage({
            content: isOversea() ? t.firstAIReplyOversea : t.firstAIReply,
            senderType: MessageSender.ROBOT,
            timestamp: Date.now(),
            type: MessageType.Text,
            headerType: HeaderType.MESSAGE,
            messageType: RobotMessageType.ROBOT_NORMAL_REPLY,
            robotData: {},
          });
        }
      } catch (err) {
        console.error("SSE发送失败:", err);
        if (onSendError) {
          onSendError(err, content);
        }
      }
    },
    [
      sendMessage,
      handleScrollToBottom,
      onSendSuccess,
      onSendError,
      hasUserSentFirstMessage,
      setRobotMessage,
      t,
    ],
  );

  return {
    handleSSESend,
    isSending: isStreaming,
    stopStreaming,
  };
}
